/**
 * Calcula el brillo perceptual promedio (0-1) de una imagen para decidir
 * automáticamente si un logo necesita un fondo claro u oscuro detrás.
 *
 *   < 0.45 → logo "oscuro" (texto/figuras negras-grises): fondo claro
 *   > 0.55 → logo "claro" (texto/figuras blancas-pastel): fondo oscuro
 *   resto  → neutro
 *
 * Implementación:
 *   - Downsampleo la imagen a 16×16 en un canvas off-DOM (suficiente
 *     resolución para promedio + rapidísimo).
 *   - Ignoro píxeles con alpha < 128 (transparentes) — un logo con
 *     fondo transparente promediado contra negro contaminaría la
 *     muestra y sesgaría a "oscuro".
 *   - Luminancia perceptual = 0.299*R + 0.587*G + 0.114*B (ITU-R BT.601).
 *
 * Returns null mientras carga o si CORS bloquea el getImageData.
 */
import { useEffect, useState } from "react";

export function useImageBrightness(url: string | null): number | null {
  const [brightness, setBrightness] = useState<number | null>(null);

  useEffect(() => {
    if (!url) {
      setBrightness(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    // El avatar viene del mismo dominio (/api/projects/:id/avatar), así
    // que CORS no aplica. Lo dejamos explícito por si en el futuro se
    // sirve desde un CDN cross-origin — habría que setear el header allá.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        const size = 16;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          setBrightness(null);
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        let sum = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3]!;
          if (alpha < 128) continue;
          const r = data[i]!;
          const g = data[i + 1]!;
          const b = data[i + 2]!;
          sum += (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          count++;
        }
        setBrightness(count > 0 ? sum / count : null);
      } catch {
        // SecurityError típico cuando el getImageData falla por CORS.
        setBrightness(null);
      }
    };
    img.onerror = () => {
      if (!cancelled) setBrightness(null);
    };
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [url]);

  return brightness;
}

/**
 * Convierte el brillo en una clase Tailwind para el fondo del avatar.
 * Diseñado para acomodar logos transparentes con la "mayor parte" del
 * trazo claro o oscuro.
 */
export function avatarBgForBrightness(brightness: number | null): string {
  if (brightness === null) {
    return "bg-gradient-to-br from-primary/20 via-base-200 to-secondary/20";
  }
  if (brightness > 0.55) {
    // Logo claro → fondo oscuro saturado para que resalte.
    return "bg-gradient-to-br from-neutral via-base-300 to-neutral";
  }
  if (brightness < 0.45) {
    // Logo oscuro → fondo claro suave.
    return "bg-gradient-to-br from-base-100 via-base-200/60 to-base-100";
  }
  // Brillo medio: gradient neutro con tinte.
  return "bg-gradient-to-br from-primary/20 via-base-200 to-secondary/20";
}
